import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Users, Award,
  Calendar, Lock, Unlock, Archive, FolderOpen, BookOpen, BarChart3, ShieldAlert
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  admis: { label: 'Admis', color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  ajourne: { label: 'Ajourne', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: RefreshCw },
  non_admissible: { label: 'Non Admissible', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

export const AcademicYearPage = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';
  const [tab, setTab] = useState(isStudent ? 'my-session' : 'second-session');

  return (
    <div className="space-y-6 animate-slide-in" data-testid="academic-year-page">
      <div>
        <h1 className="heading-2">Gestion Academique</h1>
        <p className="text-gray-500 mt-1">Deuxieme session et cloture de l'annee</p>
      </div>

      {isStudent ? (
        <StudentSecondSessionView />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="second-session" data-testid="tab-second-session">
              <RefreshCw className="w-4 h-4 mr-2 hidden sm:block" /> Deuxieme Session
            </TabsTrigger>
            <TabsTrigger value="year-management" data-testid="tab-year-management">
              <Calendar className="w-4 h-4 mr-2 hidden sm:block" /> Cloture / Ouverture
            </TabsTrigger>
          </TabsList>
          <TabsContent value="second-session" className="mt-4"><AdminSecondSessionView /></TabsContent>
          <TabsContent value="year-management" className="mt-4"><YearManagementView /></TabsContent>
        </Tabs>
      )}
    </div>
  );
};

// ============ STUDENT: My Second Session ============
const StudentSecondSessionView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/second-session/my-courses`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Card className="animate-pulse"><CardContent className="p-8"><div className="h-6 w-48 bg-gray-200 rounded" /></CardContent></Card>;

  const failed = data?.failed_courses || [];
  const dispensed = data?.dispensed_courses || [];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-700">{dispensed.length}</p>
            <p className="text-sm text-gray-500">Cours valides (dispenses)</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-red-700">{failed.length}</p>
            <p className="text-sm text-gray-500">Cours a repasser</p>
          </CardContent>
        </Card>
      </div>

      {failed.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><RefreshCw className="w-5 h-5 text-orange-600" /> Cours a repasser (Deuxieme Session)</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Cours</TableHead>
                  <TableHead className="text-center">Cr.</TableHead>
                  <TableHead className="text-center">Session 1</TableHead>
                  <TableHead className="text-center">Session 2</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failed.map(c => (
                  <TableRow key={c.course_id} data-testid={`retake-course-${c.code}`}>
                    <TableCell className="font-mono text-xs">{c.code}</TableCell>
                    <TableCell className="text-sm font-medium">{c.name}</TableCell>
                    <TableCell className="text-center">{c.credits}</TableCell>
                    <TableCell className="text-center text-red-600 font-medium">{c.session1_average}/20</TableCell>
                    <TableCell className="text-center">{c.session2_average !== undefined ? <span className={c.session2_average >= 10 ? 'text-green-600 font-bold' : 'text-red-600'}>{c.session2_average}/20</span> : <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell className="text-center">
                      {c.status === 'to_retake' && <Badge className="bg-orange-50 text-orange-700 border-orange-200">A repasser</Badge>}
                      {c.status === 'passed' && <Badge className="bg-green-50 text-green-700 border-green-200">Valide</Badge>}
                      {c.status === 'failed' && <Badge className="bg-red-50 text-red-700 border-red-200">Echec</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dispensed.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" /> Cours valides (Dispenses)</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Cours</TableHead>
                  <TableHead className="text-center">Cr.</TableHead>
                  <TableHead className="text-center">Moyenne</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispensed.map(c => (
                  <TableRow key={c.course_id}>
                    <TableCell className="font-mono text-xs">{c.code}</TableCell>
                    <TableCell className="text-sm">{c.name}</TableCell>
                    <TableCell className="text-center">{c.credits}</TableCell>
                    <TableCell className="text-center text-green-600 font-bold">{c.session1_average}/20</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {failed.length === 0 && dispensed.length === 0 && (
        <Card><CardContent className="p-10 text-center"><Award className="w-14 h-14 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Aucun resultat de session disponible pour le moment.</p></CardContent></Card>
      )}
    </div>
  );
};

// ============ ADMIN: Second Session View ============
const AdminSecondSessionView = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/online-courses/my-classrooms`).then(r => {
      setClassrooms(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchSession = useCallback(async (promoId) => {
    try {
      const res = await axios.get(`${API_URL}/api/second-session/${promoId}`);
      setSessionData(res.data);
    } catch { toast.error('Erreur'); }
  }, []);

  useEffect(() => { if (selectedPromo) fetchSession(selectedPromo); }, [selectedPromo, fetchSession]);

  const handleActivate = async () => {
    try {
      await axios.post(`${API_URL}/api/second-session/${selectedPromo}/activate`, { promotion_id: selectedPromo });
      toast.success('Deuxieme session activee — etudiants notifies');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <Card className="animate-pulse"><CardContent className="p-8"><div className="h-6 w-48 bg-gray-200 rounded" /></CardContent></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <Label>Selectionnez une promotion</Label>
          <Select value={selectedPromo || ''} onValueChange={setSelectedPromo}>
            <SelectTrigger data-testid="second-session-promo-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
            <SelectContent>
              {classrooms.map(c => <SelectItem key={c.promotion_id} value={c.promotion_id}>{c.promotion_label} ({c.academic_year})</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {sessionData && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{sessionData.stats.total_students}</p><p className="text-xs text-gray-500">Etudiants</p></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">{sessionData.stats.admis}</p><p className="text-xs text-gray-500">Admis</p></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-orange-600">{sessionData.stats.ajournes}</p><p className="text-xs text-gray-500">Ajournes</p></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-600">{sessionData.stats.non_admissibles}</p><p className="text-xs text-gray-500">Non admissibles</p></CardContent></Card>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleActivate} className="gap-2" data-testid="activate-second-session">
              <Unlock className="w-4 h-4" /> Activer la deuxieme session
            </Button>
          </div>

          {/* Student list */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matricule</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead className="text-center">Moy.</TableHead>
                    <TableHead className="text-center">Credits</TableHead>
                    <TableHead className="text-center">Echecs</TableHead>
                    <TableHead className="text-center">Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionData.students.map(st => {
                    const cfg = STATUS_CONFIG[st.status] || STATUS_CONFIG.ajourne;
                    return (
                      <TableRow key={st.student_id} data-testid={`session-student-${st.matricule}`}>
                        <TableCell className="font-mono text-xs">{st.matricule}</TableCell>
                        <TableCell className="text-sm font-medium">{st.name}</TableCell>
                        <TableCell className="text-center font-bold">{st.overall_average ?? '—'}/20</TableCell>
                        <TableCell className="text-center">{st.earned_credits}/{st.total_credits}</TableCell>
                        <TableCell className="text-center text-red-600">{st.failed_courses.length}</TableCell>
                        <TableCell className="text-center"><Badge className={cfg.color}>{cfg.label}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ============ YEAR MANAGEMENT ============
const YearManagementView = () => {
  const { user } = useAuth();
  const [showClose, setShowClose] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [closePreview, setClosePreview] = useState(null);
  const [archives, setArchives] = useState([]);
  const [closeYear, setCloseYear] = useState('2025-2026');
  const [openYear, setOpenYear] = useState('2026-2027');

  useEffect(() => {
    axios.get(`${API_URL}/api/academic-year/archives`).then(r => setArchives(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const handlePreviewClose = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic-year/preview-close?university_id=${user?.university_id || ''}&academic_year=${closeYear}`);
      setClosePreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConfirmClose = async () => {
    try {
      await axios.post(`${API_URL}/api/academic-year/close`, {
        university_id: user?.university_id || '',
        academic_year: closeYear,
        confirm: true
      });
      toast.success(`Annee ${closeYear} cloturee`);
      setShowClose(false);
      setClosePreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConfirmOpen = async () => {
    try {
      await axios.post(`${API_URL}/api/academic-year/open`, {
        university_id: user?.university_id || '',
        new_academic_year: openYear,
        confirm: true
      });
      toast.success(`Annee ${openYear} ouverte`);
      setShowOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const isAdmin = ['super_admin', 'co_admin', 'rector'].includes(user?.role);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="card-hover cursor-pointer border-2 border-dashed border-red-200 hover:border-red-400" onClick={() => setShowClose(true)}>
            <CardContent className="p-6 text-center">
              <Lock className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Cloturer l'annee</h3>
              <p className="text-sm text-gray-500">Archiver les notes, verrouiller les promotions</p>
            </CardContent>
          </Card>
          <Card className="card-hover cursor-pointer border-2 border-dashed border-green-200 hover:border-green-400" onClick={() => setShowOpen(true)}>
            <CardContent className="p-6 text-center">
              <FolderOpen className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Ouvrir une nouvelle annee</h3>
              <p className="text-sm text-gray-500">Creer les promotions pour la nouvelle annee</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Archives */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Archive className="w-5 h-5" /> Archives des annees</CardTitle></CardHeader>
        <CardContent>
          {archives.length === 0 ? (
            <p className="text-gray-400 text-center py-4">Aucune annee archivee</p>
          ) : (
            <div className="space-y-2">
              {archives.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{a.academic_year}</p>
                    <p className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString('fr-FR')} — {a.promotions_archived} promotion(s)</p>
                  </div>
                  <Badge variant="outline">Archive</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Close Year Dialog */}
      {showClose && (
        <Dialog open={true} onOpenChange={() => { setShowClose(false); setClosePreview(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-500" /> Cloturer l'annee academique</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Annee a cloturer</Label>
                <Input value={closeYear} onChange={e => setCloseYear(e.target.value)} placeholder="2025-2026" data-testid="close-year-input" />
              </div>
              <Button variant="outline" onClick={handlePreviewClose} className="gap-2 w-full" data-testid="preview-close-button">
                <BarChart3 className="w-4 h-4" /> Voir l'apercu
              </Button>
              {closePreview && (
                <div className="space-y-3">
                  <p className="text-sm font-medium">{closePreview.total_promotions} promotion(s) seront archivees :</p>
                  {closePreview.promotions.map(p => (
                    <div key={p.promotion_id} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium">{p.promotion_label}</p>
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        <span>{p.total_students} etud.</span>
                        <span className="text-green-600">{p.promoted} promus</span>
                        <span className="text-orange-600">{p.failed} ajournes</span>
                        <span className="text-red-600">{p.excluded} exclus</span>
                      </div>
                    </div>
                  ))}
                  <Button variant="destructive" onClick={handleConfirmClose} className="w-full gap-2" data-testid="confirm-close-button">
                    <Lock className="w-4 h-4" /> Confirmer la cloture
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Open Year Dialog */}
      {showOpen && (
        <Dialog open={true} onOpenChange={() => setShowOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderOpen className="w-5 h-5 text-green-500" /> Ouvrir une nouvelle annee</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Nouvelle annee academique</Label>
                <Input value={openYear} onChange={e => setOpenYear(e.target.value)} placeholder="2026-2027" data-testid="open-year-input" />
              </div>
              <p className="text-sm text-gray-500">Cette action creera automatiquement les promotions (L1-L3, M1-M2) pour tous les programmes actifs.</p>
              <Button onClick={handleConfirmOpen} className="w-full gap-2" data-testid="confirm-open-button">
                <FolderOpen className="w-4 h-4" /> Ouvrir l'annee {openYear}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AcademicYearPage;
